import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Chip,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Collapse,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight,
  ExpandMore,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import ContractPaymentModal from "./ContractPaymentModal";
import { getToken } from "services/auth.service";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import {
  formatAmount,
  formatQuantity,
  formatCurrencyWithUnit,
  formatDate,
} from "utils/formatters";
import "./ContractPaymentTable.css";
import { AppButton } from "components/common/buttons";

const ContractPaymentTable = ({
  paymentProfile,
  contractItems = [],
  onRefresh,
  isInModal = false, // Thêm prop để biết có đang trong modal không
}) => {
  const [contractPayments, setContractPayments] = useState([]);
  const [allContractPayments, setAllContractPayments] = useState([]); // Tất cả thanh toán của hợp đồng
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Permission Snackbar state
  const [permissionSnackbar, setPermissionSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const [expanded, setExpanded] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Helper function để toggle expand/collapse
  const handleToggleExpand = useCallback(
    (itemId) => {
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }
      setExpandedItems(newExpanded);
    },
    [expandedItems]
  );

  // Helper function để kiểm tra item có children
  const hasChildren = useCallback((item) => {
    return item.hasChildren || (item.children && item.children.length > 0);
  }, []);

  // Helper function để tìm đường dẫn hạng mục cha

  // Auto-expand các item có thanh toán
  useEffect(() => {
    if (contractPayments.length > 0 && contractItems.length > 0) {
      const itemsWithPayments = new Set(
        contractPayments.map((p) => p.contractItemId)
      );
      const newExpanded = new Set();

      // Expand tất cả parent của các item có thanh toán
      const expandParents = (items) => {
        items.forEach((item) => {
          if (item.children && item.children.length > 0) {
            // Kiểm tra xem có item con nào có thanh toán không
            const hasPaymentInChildren = (children) => {
              return children.some((child) => {
                if (itemsWithPayments.has(child.itemId)) return true;
                if (child.children) return hasPaymentInChildren(child.children);
                return false;
              });
            };

            if (hasPaymentInChildren(item.children)) {
              newExpanded.add(item.itemId);
            }

            expandParents(item.children);
          }
        });
      };

      expandParents(contractItems);
      setExpandedItems(newExpanded);
    }
  }, [contractPayments, contractItems]);

  // Tạo tree data để hiển thị (chỉ hiển thị hạng mục có thanh toán và parent của chúng)
  const treeData = useMemo(() => {
    if (!contractItems || contractItems.length === 0) return [];

    // Tạo Set các item có thanh toán
    const itemsWithPayments = new Set(
      contractPayments.map((p) => p.contractItemId)
    );

    // Hàm kiểm tra item có children có thanh toán không
    const hasPaymentInChildren = (item) => {
      if (itemsWithPayments.has(item.itemId)) return true;
      if (item.children && item.children.length > 0) {
        return item.children.some((child) => hasPaymentInChildren(child));
      }
      return false;
    };

    // Hàm filter để chỉ giữ lại item cần thiết
    const filterItemsWithPayments = (items) => {
      return items
        .filter((item) => {
          return hasPaymentInChildren(item);
        })
        .map((item) => ({
          ...item,
          children: item.children ? filterItemsWithPayments(item.children) : [],
        }));
    };

    // Filter contractItems để chỉ giữ lại những item cần thiết
    const filteredItems = filterItemsWithPayments(contractItems);

    // Tạo flat data từ filtered hierarchical structure
    const result = [];
    const addVisibleItems = (items, level = 0) => {
      items.forEach((item) => {
        result.push({ ...item, level_: level });
        if (
          item.children &&
          item.children.length > 0 &&
          expandedItems.has(item.itemId)
        ) {
          addVisibleItems(item.children, level + 1);
        }
      });
    };

    addVisibleItems(filteredItems);
    return result;
  }, [contractItems, contractPayments, expandedItems]);

  useEffect(() => {
    if (paymentProfile?.paymentProfileId) {
      loadContractPayments();
      loadAllContractPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProfile]);

  const loadAllContractPayments = async () => {
    if (!paymentProfile?.contractId) return;

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ContractPayment/contract/${paymentProfile.contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setAllContractPayments(result.data || []);
      } else {
        console.warn("Could not load all contract payments");
        setAllContractPayments([]);
      }
    } catch (error) {
      console.error("Error loading all contract payments:", error);
      setAllContractPayments([]);
    }
  };

  const loadContractPayments = async () => {
    if (!paymentProfile?.paymentProfileId) return;

    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ContractPayment/payment-profile/${paymentProfile.paymentProfileId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setContractPayments(result.data || []);
      } else {
        throw new Error("Failed to load contract payments");
      }
    } catch (error) {
      console.error("Error loading contract payments:", error);
      showSnackbar("Lỗi tải danh sách thanh toán hạng mục", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle add payment with permission check
  const handleAddPayment = async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_CREATE);
      if (result.hasPermission) {
        setEditingPayment(null);
        setShowModal(true);
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền tạo thanh toán hạng mục",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
    }
  };

  const handleSavePayment = async (paymentData, paymentId = null) => {
    try {
      const token = getToken();
      const url = paymentId
        ? `${process.env.REACT_APP_API_URL}/api/ContractPayment/${paymentId}`
        : `${process.env.REACT_APP_API_URL}/api/ContractPayment`;

      const method = paymentId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentData),
      });

      if (response.ok) {
        await loadContractPayments();
        await loadAllContractPayments(); // Thêm dòng này để cập nhật dữ liệu tổng hợp
        if (onRefresh) onRefresh();
        showSnackbar(
          paymentId
            ? "Cập nhật thanh toán hạng mục thành công"
            : "Thêm thanh toán hạng mục thành công"
        );
      } else {
        const error = await response.json();
        console.error("API Error response:", error); // Debug log
        throw new Error(error.message || "Lỗi khi lưu thanh toán hạng mục");
      }
    } catch (error) {
      console.error("Error saving contract payment:", error);
      showSnackbar(error.message || "Lỗi khi lưu thanh toán hạng mục", "error");
      throw error;
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa thanh toán hạng mục này?"))
      return;

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ContractPayment/${paymentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        await loadContractPayments();
        await loadAllContractPayments(); // Thêm dòng này để cập nhật dữ liệu tổng hợp
        if (onRefresh) onRefresh();
        showSnackbar("Xóa thanh toán hạng mục thành công");
      } else {
        const error = await response.json();
        throw new Error(error.message || "Lỗi khi xóa thanh toán hạng mục");
      }
    } catch (error) {
      console.error("Error deleting contract payment:", error);
      showSnackbar(error.message || "Lỗi khi xóa thanh toán hạng mục", "error");
    }
  };

  const handleMenuClick = (event, payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleEdit = async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_UPDATE);
      if (result.hasPermission) {
        setEditingPayment(selectedPayment);
        setShowModal(true);
        handleMenuClose();
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền chỉnh sửa thanh toán hạng mục",
          severity: "warning",
        });
        handleMenuClose();
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_DELETE);
      if (result.hasPermission) {
        if (selectedPayment) {
          handleDeletePayment(selectedPayment.contractPaymentId);
        }
        handleMenuClose();
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền xóa thanh toán hạng mục",
          severity: "warning",
        });
        handleMenuClose();
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
      handleMenuClose();
    }
  };

  const calculatePaymentAmount = (payment) => {
    // Ưu tiên dùng PaymentAmount từ database
    if (payment.paymentAmount && payment.paymentAmount > 0) {
      return Math.round(payment.paymentAmount);
    }

    // Fallback: tính toán nếu không có PaymentAmount từ DB
    // Chỉ tính thành tiền trước thuế (không có VAT)
    const item = contractItems.find(
      (item) => item.itemId === payment.contractItemId
    );
    if (!item) return 0;

    return Math.round(item.unitPrice * payment.paymentQuantity);
  };

  const getTotalPaymentBeforeTax = () => {
    return Math.round(
      contractPayments.reduce((total, payment) => {
        return total + calculatePaymentAmount(payment);
      }, 0)
    );
  };

  const getTotalPaymentAfterTax = () => {
    const beforeTax = getTotalPaymentBeforeTax();
    const vatAmount = beforeTax * (paymentProfile.vat / 100);
    return Math.round(beforeTax + vatAmount);
  };

  // Helper function để tìm item trong cây hierarchical
  const findItemInTree = (items, targetId) => {
    for (const item of items) {
      if (item.itemId === targetId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findItemInTree(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Tính khối lượng còn lại chưa thanh toán cho một hạng mục (tổng tất cả các đợt)
  const getRemainingQuantity = (contractItemId) => {
    try {
      

      if (!contractItems || contractItems.length === 0) {
        console.warn(`No contractItems available`);
        return 0;
      }

      // Sử dụng findItemInTree thay vì find thông thường
      const item = findItemInTree(contractItems, contractItemId);
     

      if (!item) {
        console.warn(`Item not found for ID: ${contractItemId}`);
        return 0;
      }

   
      

      // Tính tổng khối lượng đã thanh toán cho hạng mục này từ TẤT CẢ các kỳ thanh toán
      const relevantPayments = allContractPayments.filter((payment) => {
        return payment.contractItemId === contractItemId;
      });

    

      const totalPaidQuantity = relevantPayments.reduce(
        (total, payment) => total + parseFloat(payment.paymentQuantity || 0),
        0
      );

   

      // Khối lượng còn lại = Khối lượng hợp đồng - Tổng đã thanh toán (tất cả các đợt)
      const remaining = (item.contractQuantity || 0) - totalPaidQuantity;

     

      return Math.max(0, remaining); // Không để âm
    } catch (error) {
      console.error(
        `Error in getRemainingQuantity for ID ${contractItemId}:`,
        error
      );
      return 0;
    }
  };

  // Tạo payment map để tối ưu hiệu suất
  const paymentMap = useMemo(() => {
    const map = new Map();
    contractPayments.forEach((payment) => {
      map.set(payment.contractItemId, payment);
    });
    return map;
  }, [contractPayments]);

  const columns = [
    {
      accessorKey: "itemCode",
      header: "Mã hạng mục",
      size: 120,
      Cell: ({ row }) => {
        const item = row.original;
        const level = item.level_ || 0;
        const indent = level * 20;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${indent}px`,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                mr: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hasChildren(item) ? (
                <IconButton
                  size="small"
                  onClick={() => handleToggleExpand(item.itemId)}
                  sx={{ p: 0.5 }}
                >
                  {expandedItems.has(item.itemId) ? (
                    <ExpandMore sx={{ fontSize: "1rem" }} />
                  ) : (
                    <ChevronRight sx={{ fontSize: "1rem" }} />
                  )}
                </IconButton>
              ) : null}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: level === 0 ? 600 : 400,
                fontFamily: "monospace",
                color: "#1976d2",
                fontSize: "0.75rem",
              }}
            >
              {item.itemCode || "N/A"}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "itemName",
      header: "Hạng mục",
      size: 300,
      Cell: ({ row }) => {
        const item = row.original;
        const level = item.level_ || 0;

        return (
          <Box>
            {/* Tên hạng mục */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: level === 0 ? 600 : 400,
                fontSize: "0.75rem",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {item.itemName && item.itemName.trim() !== ""
                ? item.itemName
                : `[${item.itemCode || "N/A"}]`}
            </Typography>

            {/* Thông tin đơn giá cho leaf nodes */}
            {!hasChildren(item) && item.unitPrice && (
              <Typography variant="caption" color="text.secondary">
                Đơn giá: {formatAmount(item.unitPrice)} /{" "} Độ khó khăn: {" "}
                {item.difficultyLevel || "N/A"}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: "paymentQuantity",
      header: "Khối lượng TT",
      size: 120,
      Cell: ({ row }) => {
        const item = row.original;

        // Chỉ hiển thị cho leaf nodes có thanh toán
        if (hasChildren(item)) return null;

        const payment = paymentMap.get(item.itemId);
        if (!payment) return null;

        return (
          <Box textAlign="right">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: "0.75rem" }}
            >
              {formatQuantity(parseFloat(payment.paymentQuantity || 0))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.unit}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "amount",
      header: "Thành tiền",
      size: 130,
      Cell: ({ row }) => {
        const item = row.original;

        // Chỉ hiển thị cho leaf nodes có thanh toán
        if (hasChildren(item)) return null;

        const payment = paymentMap.get(item.itemId);
        if (!payment) return null;

        const amount = calculatePaymentAmount(payment);
        return (
          <Box textAlign="right">
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: "0.75rem" }}
            >
              {formatAmount(amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (Trước thuế)
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "remainingQuantity",
      header: "KL TT còn lại hiện tại",
      size: 140,
      Cell: ({ row }) => {
        const item = row.original;

        // Chỉ hiển thị cho leaf nodes có thanh toán
        if (hasChildren(item)) {
          return null;
        }

        const payment = paymentMap.get(item.itemId);
        if (!payment) {
          return null;
        }

        const remaining = getRemainingQuantity(item.itemId);
        const isCompleted = remaining === 0;
        const isLow =
          remaining > 0 && remaining <= (item?.contractQuantity * 0.1 || 0);

        return (
          <Box textAlign="right">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: isCompleted ? "#4caf50" : isLow ? "#ff9800" : "#666",
                fontSize: "0.75rem",
              }}
            >
              {formatQuantity(remaining)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item?.unit}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "notes",
      header: "Ghi chú",
      size: 200,
      Cell: ({ row }) => {
        const item = row.original;

        // Chỉ hiển thị cho leaf nodes có thanh toán
        if (hasChildren(item)) return null;

        const payment = paymentMap.get(item.itemId);
        if (!payment) return null;

        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            {payment.notes || "—"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Ngày tạo",
      size: 120,
      enableHiding: true,
      Cell: ({ row }) => {
        const item = row.original;

        // Chỉ hiển thị cho leaf nodes có thanh toán
        if (hasChildren(item)) return null;

        const payment = paymentMap.get(item.itemId);
        if (!payment) return null;

        const date = dayjs(payment.createdAt);
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            {/* {date.format("DD/MM/YYYY")} */}
            {formatDate(payment.createdAt)}
          </Typography>
        );
      },
    },
    {
      id: "actions",
      header: "",
      size: 60,
      enableSorting: false,
      Cell: ({ row }) => {
        const item = row.original;

        // Chỉ hiển thị cho leaf nodes có thanh toán
        if (hasChildren(item)) return null;

        const payment = paymentMap.get(item.itemId);
        if (!payment) return null;

        return (
          <IconButton
            size="small"
            onClick={(e) => handleMenuClick(e, payment)}
            sx={{ color: "#666" }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  if (!paymentProfile) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Vui lòng chọn một kỳ thanh toán để xem chi tiết
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header - chỉ hiển thị khi không trong modal */}
      {!isInModal && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ color: "#666" }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography
              variant="h6"
              sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
            >
              Chi tiết thanh toán: {paymentProfile.paymentName}
            </Typography>
            <Chip
              label={dayjs(paymentProfile.paymentDate).format("DD/MM/YYYY")}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Box>
          <AppButton
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAddPayment}
            disabled={!expanded}
          >
            Thêm thanh toán
          </AppButton>
        </Box>
      )}

      {/* Khi trong modal, hiển thị button thêm thanh toán ở trên cùng */}
      {isInModal && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
            px: 3,
            pt: 2,
          }}
        >
          <AppButton
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAddPayment}
          >
            Thêm thanh toán
          </AppButton>
        </Box>
      )}

      <Collapse in={isInModal ? true : expanded}>
        <Paper
          sx={{
            border: "1px solid #e0e0e0",
            boxShadow: "none",
            elevation: 0,
          }}
          className="payment-table"
        >
          <MaterialReactTable
            columns={columns}
            data={treeData}
            state={{
              isLoading: loading,
              columnVisibility: { createdAt: false },
            }}
            enableColumnFilters={false}
            enableGlobalFilter={false}
            enableDensityToggle={false}
            enableFullScreenToggle={true}
            enableHiding={true}
            enableColumnActions={false}
            enablePagination={false}
            enableBottomToolbar={contractPayments.length > 0}
            renderBottomToolbar={() =>
              contractPayments.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    borderTop: "1px solid #e0e0e0",
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Tổng số thanh toán: {contractPayments.length} | Hiển thị:{" "}
                    {treeData.length} hạng mục
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="body2" color="text.secondary">
                        Tổng trước thuế:
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {formatCurrencyWithUnit(getTotalPaymentBeforeTax())}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="body2" color="text.secondary">
                        Tổng sau thuế ({paymentProfile.vat}% VAT):
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {formatCurrencyWithUnit(getTotalPaymentAfterTax())}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )
            }
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#f8f9fa !important",
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "#333",
                borderBottom: "2px solid #e0e0e0",
                position: "sticky !important",
                top: "0px !important",
                zIndex: "1300 !important",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1) !important",
              },
            }}
            muiTableProps={{
              stickyHeader: true,
              sx: {
                "& .MuiTableBody-root": {
                  "& .MuiTableRow-root": {
                    position: "relative",
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  },
                  "& .MuiTableCell-root": {
                    position: "relative",
                    zIndex: 1,
                  },
                },
                boxShadow: "none",
                elevation: 0,
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: isInModal ? "60vh" : "400px", // Tăng height khi trong modal
                minHeight: isInModal ? "400px" : "200px",
                overflowY: "auto",
                overflowX: "auto",
                border: "none",
                borderRadius: 0,
                boxShadow: "none",
                position: "relative",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#a8a8a8",
                  },
                },
              },
            }}
            muiTablePaperProps={{
              sx: {
                boxShadow: "none",
                elevation: 0,
                border: "none",
              },
            }}
            muiTableBodyProps={{
              sx:
                contractPayments.length === 0
                  ? {
                      "& .MuiTableRow-root": {
                        "& .MuiTableCell-root": {
                          textAlign: "center",
                          py: 4,
                        },
                      },
                    }
                  : {},
            }}
            localization={{
              noRecordsToDisplay: "Chưa có thanh toán nào cho kỳ này",
            }}
          />
        </Paper>
      </Collapse>

      {/* Menu Actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: "none",
            border: "1px solid #e0e0e0",
            elevation: 0,
          },
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: "1.125rem" }} />
          Chỉnh sửa
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#d32f2f" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: "1.125rem" }} />
          Xóa
        </MenuItem>
      </Menu>

      {/* Contract Payment Modal */}
      <ContractPaymentModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPayment(null);
        }}
        onSave={handleSavePayment}
        paymentProfile={paymentProfile}
        contractItems={contractItems}
        existingPayments={contractPayments}
        allContractPayments={allContractPayments}
        editingPayment={editingPayment}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontFamily: "Inter, sans-serif" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Permission Snackbar */}
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={3500}
        onClose={() =>
          setPermissionSnackbar({ ...permissionSnackbar, open: false })
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setPermissionSnackbar({ ...permissionSnackbar, open: false })
          }
          severity={permissionSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractPaymentTable;
